import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, from, switchMap, BehaviorSubject, throwError, catchError, tap } from 'rxjs';
import { KeycloakService } from '../services/keycloak.service';
import { API_ENDPOINT } from './env';
import { PendingRequestsService } from './pending-requests.service';

/**
 * Compatibility wrapper around HttpClient.
 *
 * Services that inject Http (old @angular/http) now inject HttpClient via this wrapper.
 * The interceptor handles auth and URL prefixing, so this class simply delegates.
 *
 * The `blobQuery` and `postBlobQuery` methods are kept for blob downloads
 * which need special handling via XMLHttpRequest.
 */
@Injectable({ providedIn: 'root' })
export class HttpWanaka {
  public i18nAcceptLanguageHeader: string;

  constructor(
    private http: HttpClient,
    private pendingRequests: PendingRequestsService,
  ) {}

  get pending$(): BehaviorSubject<number> {
    return this.pendingRequests.pending$;
  }

  /**
   * HTTP GET. URL is relative to API endpoint.
   * The auth interceptor handles token and URL prefixing.
   */
  get(url: string, options?: any): Observable<any> {
    return this.http.get(url, options);
  }

  post(url: string, body: any, options?: any): Observable<any> {
    return this.http.post(url, body, options);
  }

  put(url: string, body: any, options?: any): Observable<any> {
    return this.http.put(url, body, options);
  }

  delete(url: string, options?: any): Observable<any> {
    return this.http.delete(url, options);
  }

  patch(url: string, body: any, options?: any): Observable<any> {
    return this.http.patch(url, body, options);
  }

  head(url: string, options?: any): Observable<any> {
    return this.http.head(url, options);
  }

  /**
   * Download a blob via XMLHttpRequest (for file downloads).
   * The interceptor does not handle blob response types natively.
   */
  blobQuery(url: string, applicationType: string): Observable<Blob> {
    return from(KeycloakService.updateToken()).pipe(
      switchMap((token) => {
        return new Observable<Blob>((subscriber) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', API_ENDPOINT + url, true);
          xhr.responseType = 'blob';
          xhr.setRequestHeader('Authorization', 'Bearer ' + token);
          if (this.i18nAcceptLanguageHeader) {
            xhr.setRequestHeader('Accept-Language', this.i18nAcceptLanguageHeader);
          }
          xhr.onreadystatechange = function () {
            if (xhr.readyState === 4) {
              if (xhr.status === 200) {
                const blob = new Blob([this.response], { type: applicationType });
                subscriber.next(blob);
                subscriber.complete();
              } else {
                subscriber.error(xhr);
              }
            }
          };
          xhr.send();
        });
      }),
      catchError((e) => {
        KeycloakService.auth.authz.logout();
        return throwError(() => e);
      }),
    );
  }

  /**
   * POST with blob response (for file downloads with payload).
   */
  postBlobQuery(url: string, payload: any, applicationType: string): Observable<Blob> {
    return from(KeycloakService.updateToken()).pipe(
      switchMap((token) => {
        return new Observable<Blob>((subscriber) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', API_ENDPOINT + url, true);
          xhr.responseType = 'blob';
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Authorization', 'Bearer ' + token);
          if (this.i18nAcceptLanguageHeader) {
            xhr.setRequestHeader('Accept-Language', this.i18nAcceptLanguageHeader);
          }
          xhr.onreadystatechange = function () {
            if (xhr.readyState === 4) {
              if (xhr.status === 200) {
                const blob = new Blob([this.response], { type: applicationType });
                subscriber.next(blob);
                subscriber.complete();
              } else {
                subscriber.error(xhr);
              }
            }
          };
          xhr.send(JSON.stringify(payload));
        });
      }),
      catchError((e) => {
        KeycloakService.auth.authz.logout();
        return throwError(() => e);
      }),
    );
  }
}
